// parse.js
// Parses the compact prefix-join filter syntax into a JSON tree.
// Distinguishes JOIN(rule) vs JOIN(group), so UI can reconstruct original shape.
// Supports settings in canonical order: logic, period, range, count (any subset).
// Value parsing respects logic: value+to for range/between, value+tolerance for tolerance/someof.
// "to" and "tolerance" are never used together.

import $filter from '../filter'; // optional metric schema registry

function makeId() {
  return Math.random().toString(36).substring(2, 8);
}

function splitTopLevel(str, delim = ';') {
  const out = [];
  let depthPar = 0, depthBrace = 0, buf = '';
  for (let i = 0; i < str.length; i++) {
    const ch = str[i];
    if (ch === '(') depthPar++;
    else if (ch === ')') depthPar = Math.max(0, depthPar - 1);
    else if (ch === '{') depthBrace++;
    else if (ch === '}') depthBrace = Math.max(0, depthBrace - 1);

    if (ch === delim && depthPar === 0 && depthBrace === 0) {
      if (buf.trim()) out.push(buf.trim());
      buf = '';
    } else {
      buf += ch;
    }
  }
  if (buf.trim()) out.push(buf.trim());
  return out;
}

function stripOuter(str, left, right) {
  const s = str.trim();
  if (s.startsWith(left) && s.endsWith(right)) {
    let depth = 0;
    for (let i = 0; i < s.length; i++) {
      const ch = s[i];
      if (ch === left) depth++;
      else if (ch === right) {
        depth--;
        if (depth === 0 && i !== s.length - 1) return s; // not a single wrapper
      }
    }
    if (depth === 0) return s.slice(left.length, s.length - right.length).trim();
  }
  return s;
}

function normalizeOp(op) {
  switch (op) {
    case 'e':  return '=';
    case '>e': return '>=';
    case '<e': return '<=';
    case '!e':
    case 'ne': return '!=';
    default:   return op;
  }
}

function getTypeDef(metric) {
  return $filter?.type?.[metric];
}

function isSetMetric(metric) {
  const def = getTypeDef(metric);
  return !!def && def.type === 'set';
}

function looksNumeric(s) {
  if (typeof s === 'number') return Number.isFinite(s);
  if (typeof s !== 'string') return false;
  if (!s.length) return false;
  const n = Number(s);
  return Number.isFinite(n);
}

// ---------- Settings parsing (multi-slot) ----------
const SETTING_ORDER = ['logic', 'period', 'range', 'count', 'rating'];

function getSupportedSettingKeys(metric) {
  const def = getTypeDef(metric);
  const cfg = def?.setting;
  if (!cfg) return new Set();
  if (Array.isArray(cfg)) {
    return new Set(cfg.filter(k => SETTING_ORDER.includes(k)));
  }
  // object form: { logic:true, period:true, ... }
  return new Set(SETTING_ORDER.filter(k => cfg && cfg[k]));
}

/**
 * Map tokens left-to-right onto supported keys in canonical order:
 * logic, period, range, count
 * - Skips unsupported keys
 * - Coerces count to Number when numeric
 */
function mapSettings(metric, tokens) {
  const supported = getSupportedSettingKeys(metric);
  if (!supported.size || !tokens || !tokens.length) return {};

  const setting = {};
  let idx = 0;

  for (const key of SETTING_ORDER) {
    if (!supported.has(key)) continue;
    const tok = tokens[idx++];
    if (tok == null || tok === '') continue;

    if (key === 'count' && looksNumeric(tok)) setting.count = Number(tok);
    else setting[key] = tok;

    if (idx >= tokens.length) break;
  }
  return setting;
}

// contains decoding helpers (logic variant)
function isContains(setting) {
  return String(setting?.logic ?? '').toLowerCase() === 'contains';
}
function safeDecode(s) {
  try { return decodeURIComponent(s); } catch { return s; }
}

function parseRuleExpr(expr) {
  let linksBlock = '', rulePart = expr;

  // peel trailing { ... } at top-level
  if (expr.endsWith('}')) {
    let depthPar = 0, depthBrace = 0, bracePos = -1;
    for (let i = expr.length - 1; i >= 0; i--) {
      const ch = expr[i];
      if (ch === ')') depthPar++;
      else if (ch === '(') depthPar--;
      else if (ch === '}') depthBrace++;
      else if (ch === '{') {
        depthBrace--;
        if (depthBrace === 0 && depthPar === 0) { bracePos = i; break; }
      }
    }
    if (bracePos >= 0) {
      linksBlock = expr.slice(bracePos);
      rulePart   = expr.slice(0, bracePos);
    }
  }

  const metricMatch = rulePart.match(/^([A-Za-z0-9._-]+)(\[[^\]]*\])?(.*)$/);
  if (!metricMatch) return null;

  const metric = metricMatch[1];
  const settingsRaw = metricMatch[2] || '';
  let rest = (metricMatch[3] || '').trim();

  // settings
  let setting = {};
  if (settingsRaw) {
    const inner = stripOuter(settingsRaw, '[', ']');
    const tokens = inner ? inner.split(',').map(s => s.trim()) : [];
    setting = mapSettings(metric, tokens);
  }

  // operator
  const opMatch = rest.match(/^(>=|<=|!=|>e|<e|!e|ne|>|<|e)/);
  if (!opMatch) return null;
  const operand = normalizeOp(opMatch[1]);
  rest = rest.slice(opMatch[1].length).trim();

  // values
  if (!rest.startsWith('[')) return null;
  const valuesRaw = stripOuter(rest, '[', ']');
  let valueTokens = valuesRaw ? splitTopLevel(valuesRaw, ',').map(s => s.trim()).filter(v => v.length) : [];

  // If "contains", fully decode all tokens (single or array)
  if (isContains(setting) && valueTokens.length) {
    valueTokens = valueTokens.map(safeDecode);
  }

  const logic = { operand };
  const sLogic = String(setting.logic || '').toLowerCase();

  // ----- VALUE ASSIGNMENT RULES -----
  // Set metrics: always array, but "someof" allows a trailing tolerance
  if (isSetMetric(metric)) {
    if (sLogic === 'someof') {
      if (valueTokens.length === 0) {
        logic.value = [];
      } else if (valueTokens.length === 1) {
        // Edge: only tolerance given (no items). Keep items empty, store tolerance.
        logic.value = [];
        logic.tolerance = valueTokens[0];
      } else {
        // Last token is tolerance, the rest are the set members.
        logic.tolerance = valueTokens[valueTokens.length - 1];
        logic.value = valueTokens.slice(0, -1);
      }
    } else {
      logic.value = valueTokens; // [] | ['home'] | ['home','office']
    }
  } else {
    // Scalar metrics:
    //  - For range/between logic: value + to
    //  - For tolerance/someof logic: value + tolerance
    //  - Otherwise: single -> scalar; multi -> array (no guessing "to" vs "tolerance")
    if ((sLogic === 'range' || sLogic === 'between') && valueTokens.length >= 2) {
      logic.value = valueTokens[0];
      logic.to = valueTokens[1]; // "to" only with range/between
    } else if ((sLogic === 'tolerance' || sLogic === 'someof') && valueTokens.length >= 2) {
      logic.value = valueTokens[0];
      logic.tolerance = valueTokens[1]; // "tolerance" with tolerance/someof
    } else {
      if (valueTokens.length === 1) {
        logic.value = valueTokens[0];
      } else if (valueTokens.length > 1) {
        logic.value = valueTokens;
      }
    }
  }
  // ----- END VALUE ASSIGNMENT -----

  // links (optional)
  let links = {};
  if (linksBlock) {
    const inner = stripOuter(linksBlock, '{', '}');
    const parts = splitTopLevel(inner, ';');
    const arr = parts.map(parseRuleExpr).filter(Boolean).map(n => n.rule);
    if (arr.length) links = arr;
  }

  return {
    id: makeId(),
    logic: 'and', // node's own join default; parent determines actual joining
    rule: { type: metric, setting, logic },
    links
  };
}

/**
 * Parse a JOIN(...) wrapper. Inside may be a single RULE or a SEQ (group).
 */
function parseJoinWrapper(joinWord, innerRaw) {
  const logic = joinWord.toLowerCase();
  const innerTrim = innerRaw.trim();

  // If inner is a parenthesized SEQ "( ... )" → group
  const innerMaybeStripped = (innerTrim.startsWith('(') && innerTrim.endsWith(')'))
    ? stripOuter(innerTrim, '(', ')')
    : innerTrim;

  // Top-level semicolons imply SEQ → group
  const parts = splitTopLevel(innerMaybeStripped, ';');

  if (parts.length === 1 && !(innerTrim.startsWith('(') && innerTrim.endsWith(')'))) {
    // Single piece and not an explicit "( ... )" → try parse as RULE
    const maybeRule = parseRuleExpr(innerTrim);
    if (maybeRule) {
      // JOIN(rule)
      maybeRule.logic = logic; // join to parent is the joinWord
      return maybeRule;
    }
    // If it's not a valid rule, fall through to group handling.
  }

  // JOIN(group): items are parts parsed individually
  return {
    id: makeId(),
    logic, // join to parent
    rules: parts.map(parseItem).filter(Boolean)
  };
}

/**
 * Parse a single item:
 *   - "and(...)", "or(...)", "nand(...)", "nor(...)": wrapper; inner can be rule OR group
 *   - "( ... )": implicit AND group
 *   - "ruleToken": leaf rule
 */
function parseItem(item) {
  const s = item.trim();

  // explicit join wrapper
  const m = s.match(/^(and|or|nand|nor)\(([\s\S]*)\)$/i);
  if (m) return parseJoinWrapper(m[1], m[2]);

  // implicit AND group
  if (s.startsWith('(') && s.endsWith(')')) {
    const inner = stripOuter(s, '(', ')');
    const children = inner ? splitTopLevel(inner, ';') : [];
    return { id: makeId(), logic: 'and', rules: children.map(parseItem).filter(Boolean) };
  }

  // leaf rule
  return parseRuleExpr(s);
}

/**
 * Root: supports multiple top-level items separated by ';'
 */
function parseRoot(expr) {
  const trimmed = (expr || '').trim();
  if (!trimmed) return { id: makeId(), logic: 'and', rules: [] };

  const parts = splitTopLevel(trimmed, ';');

  if (parts.length > 1) {
    return {
      id: makeId(),
      logic: 'and',
      rules: parts.map(parseItem).filter(Boolean)
    };
  }

  const node = parseItem(trimmed);
  if (!node || node.rule) {
    return { id: makeId(), logic: 'and', rules: node ? [node] : [] };
  }
  return node;
}

export default {
  run(expr) {
    return parseRoot(expr);
  }
};
